// This PC (decision 065, F422; laid out in named sections since F499,
// decision 105): what the companion has on this PC, what is happening,
// the set-up, where its files are. The aircraft live on the Fleet, the
// steps on Set up. Under the host alone.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { cancelQuestion, TransferBar } from "@/components/OnThisPc";
import { useHostEvents } from "@/host/events";
import { sessionLine, watchLine } from "@/host/format";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import type { AppInfo, SessionMessage } from "@/host/types";

export const Route = createFileRoute("/pc/")({
  component: ThisPc,
});

function ThisPc() {
  const host = useHost()!;
  const info = useQuery(hostQueries.info(host));
  const status = useQuery(hostQueries.status(host));
  if (!info.data) return <p className="muted">Reading this PC...</p>;
  const i = info.data;
  const rows = status.data ?? [];
  const pluginState =
    i.plugin_installed === null
      ? { value: "unknown yet", note: "Known once the airframes folder is: the plugin lives beside it, in X-Plane.", tone: "plain" as const }
      : !i.plugin_installed
        ? { value: "not found", note: "Set up installs it into every X-Plane 12 on this PC, X-Plane closed.", tone: "warn" as const }
        : i.plugin_current === false
          ? { value: `installed, older than the newest released${i.plugin_ready ? ` (${i.plugin_ready})` : ""}`, note: "Install it again from Set up, X-Plane closed.", tone: "warn" as const }
          : i.plugin_behind
            ? { value: `installed, ${i.plugin_behind.found} in the cockpit while ${i.plugin_behind.latest} is released`, note: "Install it again from Set up, X-Plane closed.", tone: "warn" as const }
            : { value: "installed", note: "It shows who has the aircraft in the cockpit, draws the wear, records the flight.", tone: "ok" as const };
  // The platform's minimums (F408), said with the companion whatever its state.
  const minimums = i.compat ? `The platform takes companion ${i.compat.companion_min ?? "any"} and plugin ${i.compat.plugin_min ?? "any"} or newer.` : "";
  const companionState = !i.compat
    ? { value: `${i.version}, the platform not asked yet`, note: "The platform says which companion and plugin it still works with.", tone: "plain" as const }
    : !i.compat.companion_ok
      ? { value: `${i.version}, older than the platform takes`, note: `${minimums} Update from Settings before booking anything.`, tone: "warn" as const }
      : !i.compat.companion_current && i.compat.companion_latest
        ? { value: `${i.version}, ${i.compat.companion_latest} is out`, note: `${minimums} Settings, Check for updates, Download, Install; nothing updates by itself.`, tone: "plain" as const }
        : { value: `${i.version}, the newest`, note: minimums, tone: "ok" as const };
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="page-title">This PC</h1>
          <p className="muted text-sm">What the companion has on this PC and what it is doing. The aircraft are on the Fleet; the first evening's steps on Set up.</p>
        </div>
        <span className="flex-1" />
        <span className="muted text-xs" data-testid="pc-version">
          companion {i.version}
          {i.compat && i.compat.companion_ok && !i.compat.companion_current && i.compat.companion_latest ? (
            <span className="text-warn" data-testid="latest-out">
              {" "}
              ({i.compat.companion_latest} out)
            </span>
          ) : null}
        </span>
      </header>

      <section className="space-y-3" aria-labelledby="pc-has">
        <div>
          <h2 id="pc-has" className="text-[1.0714rem] font-semibold">
            What this PC has
          </h2>
          <p className="muted text-xs">What ShariFly needs here, each with its state.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2" aria-label="status bar">
          <Fact
            label="The airframes folder"
            value={i.airframes_dir ? `${i.airframes_dir} (${rows.length} airframes)` : "not found"}
            note={i.airframes_dir ? "Read from your X-Plane installations. Accepting an aircraft copies a whole folder in; returning it copies the folder out." : (i.airframes_dir_error ?? "No X-Plane 12 with the aircraft found; Settings takes the folder by hand.")}
            tone={i.airframes_dir ? "ok" : "warn"}
          />
          <Fact label="The plugin in X-Plane" value={pluginState.value} note={pluginState.note} tone={pluginState.tone} />
          <Fact label="The companion" value={companionState.value} note={companionState.note} tone={companionState.tone} />
        </div>
      </section>

      <section className="space-y-3" aria-labelledby="pc-now">
        <div>
          <h2 id="pc-now" className="text-[1.0714rem] font-semibold">
            What is happening
          </h2>
          <p className="muted text-xs">The flight the companion watches, its events, and the way to stop it or to cancel the booking.</p>
        </div>
        <SessionCard info={i} />
      </section>

      <section className="space-y-3" aria-labelledby="pc-setup">
        <div>
          <h2 id="pc-setup" className="text-[1.0714rem] font-semibold">
            The set-up
          </h2>
          <p className="muted text-xs">Five steps from the install to the first flight together, ticked from what the companion sees.</p>
        </div>
        <div className="card flex flex-wrap items-center gap-3">
          <span className="min-w-0 flex-1 text-sm">Sign in, the plugin, the folder, an airframe shared or joined, a first booking. The page stays until you hide it and comes back when a step comes undone.</span>
          <Link to="/setup" className="btn btn-sm" data-testid="pc-setup-link">
            Open Set up
          </Link>
        </div>
      </section>

      <section className="space-y-3" aria-labelledby="pc-where">
        <div>
          <h2 id="pc-where" className="text-[1.0714rem] font-semibold">
            Where its files are
          </h2>
          <p className="muted text-xs">Nothing of the aircraft add-on's own files is ever modified: the companion copies whole folders, only while X-Plane is closed.</p>
        </div>
        <dl className="card grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]" data-testid="pc-files">
          <dt className="muted">Home</dt>
          <dd className="break-all">{i.home}: a backup of every booking, the returns waiting to be sent, the held ones.</dd>
          <dt className="muted">Session</dt>
          <dd>kept in the {i.token_store}.</dd>
          <dt className="muted">Log</dt>
          <dd className="break-all">
            {i.log_path}{" "}
            <Link to="/pc/log" className="underline">
              read it here
            </Link>
            .
          </dd>
        </dl>
      </section>
    </div>
  );
}

function Fact({ label, value, note, tone }: { label: string; value: string; note: string; tone: "ok" | "warn" | "plain" }) {
  return (
    <div className="card flex flex-col gap-1 !py-3">
      <span className="text-[0.9286rem] font-semibold">{label}</span>
      <span className={`truncate text-sm ${tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : ""}`} title={value}>
        {value}
      </span>
      <span className="muted text-xs">{note}</span>
    </div>
  );
}

/** The running session as the shell reports it, newest last; a word when none ran. */
function SessionCard({ info }: { info: AppInfo }) {
  const host = useHost()!;
  const events = useHostEvents();
  const queryClient = useQueryClient();
  const [stopError, setStopError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const stop = useMutation({
    mutationFn: () => host.flyStop(),
    onSuccess: () => {
      setStopError(null);
      queryClient.invalidateQueries({ queryKey: ["host"] });
    },
    onError: (e) => setStopError(String(e)),
  });
  // The booking cancelled from the session's card (F461): the watch stops, then the release; nothing is written.
  const cancel = useMutation({
    mutationFn: async (uuid: string) => {
      await host.flyStop();
      return host.release(uuid);
    },
    onSuccess: () => {
      setStopError(null);
      queryClient.invalidateQueries({ queryKey: ["host"] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
    onError: (e) => setStopError(String(e)),
  });
  const session = info.session;
  if (!session && events.session.length === 0) {
    return (
      <p className="card muted text-sm" data-testid="session-none">
        Nothing is running. Book an aircraft on the Fleet and the companion watches X-Plane from here.
      </p>
    );
  }
  // A colleague's flight watched (F405) is not a flight of ours.
  const watching = session?.watch ?? false;
  const transfer = session ? (events.transfers[session.hs_uuid] ?? null) : null;
  return (
    <section className="card space-y-2" aria-label="session" data-testid="session">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-[1.0714rem] font-semibold">{session ? `${watching ? "Watching the flight of" : "Flying"} ${session.reg}` : "The last session"}</h3>
        {session ? <span className="pill pill-ok">session running</span> : null}
        <span className="flex-1" />
        {session ? (
          <button className="btn btn-sm" onClick={() => stop.mutate()} disabled={stop.isPending || cancel.isPending}>
            {watching ? "Stop watching" : "Stop watching X-Plane (the aircraft stays yours)"}
          </button>
        ) : null}
        {session && !watching ? (
          <button className="btn btn-sm" onClick={() => setLeaving(true)} disabled={stop.isPending || cancel.isPending} data-testid="session-cancel">
            Cancel the booking
          </button>
        ) : null}
      </div>
      {leaving && session && !watching ? (
        <div className="alert alert-warn flex flex-wrap items-center gap-2 text-sm" role="alertdialog" aria-label="Cancelling the booking" data-testid="leaving">
          <span className="flex-1">{cancelQuestion(session.reg, " The watch stops first.")}</span>
          <button type="button" className="btn btn-sm" onClick={() => setLeaving(false)} data-testid="leaving-stay">
            Keep flying
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => {
              setLeaving(false);
              cancel.mutate(session.hs_uuid);
            }}
            data-testid="leaving-go"
          >
            Yes, cancel the booking
          </button>
        </div>
      ) : null}
      {stopError ? <p className="alert text-xs">{stopError}</p> : null}
      {transfer ? <TransferBar transfer={transfer} /> : null}
      <p className="muted text-xs">
        {watching
          ? "Watching a flight installs the state of whoever has the aircraft here read-only (X-Plane closed), then writes the dirt of their flight into your cockpit every five seconds. Nothing to book or return: it ends with their flight or when you stop, and the next aircraft you book replaces the files."
          : "A session keeps the aircraft yours, renews that every minute, watches X-Plane and the airframe folder, and uploads the aircraft once X-Plane is closed (or the aircraft unloaded) and the files have been quiet for fifteen seconds. Close X-Plane when you are parked and shut down."}
      </p>
      <ol className="mono flex max-h-64 flex-col gap-0.5 overflow-auto rounded-md border border-line bg-paper-2 p-2" aria-label="session events">
        {events.session.length === 0 ? <li className="muted">nothing yet</li> : null}
        {events.session.map((m, i) => (
          <li key={i}>{line(m)}</li>
        ))}
      </ol>
    </section>
  );
}

function line(m: SessionMessage): string {
  switch (m.kind) {
    case "started":
      return `session started on ${m.reg}`;
    case "event":
      return sessionLine(m.event);
    case "done":
      return m.summary;
    case "failed":
      return `ended: ${m.error}`;
    case "stopped":
      return "stopped by you";
    case "watch_started":
      return `watching the flight of ${m.reg}`;
    case "watch":
      return watchLine(m.event);
  }
}
